import React, { useState, useEffect } from 'react';
import './Materials.css';
import {
    Box,
    Button,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Select,
    MenuItem,
    FormControl,
    IconButton,
    Alert,
    InputAdornment
} from '@mui/material';
import { Add, Edit, Delete, Search, ShoppingCart } from '@mui/icons-material';
import { materialsApi, Material } from '../services/materialsApi';
import GenericPage from '../../Commun/components/GenericPage';
import { MaterialEditDialog, MaterialType } from '../components/MaterialEditDialog';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { useSystemParameters } from '../../Commun/contexts/SystemParametersContext';

const Materials: React.FC = () => {
    const { t } = useLanguage();
    
    const { getParam } = useSystemParameters();
    const currencySymbol = getParam('Currency_Symbol', 'DZD');
    const [materials, setMaterials] = useState<Material[]>([]);
    const [filteredMaterials, setFilteredMaterials] = useState<Material[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
    const [materialToDelete, setMaterialToDelete] = useState<Material | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState<MaterialType | 'all'>('all');

    useEffect(() => {
        loadMaterials();
    }, []);

    useEffect(() => {
        filterMaterials();
    }, [materials, searchTerm, filterType]);

    const loadMaterials = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await materialsApi.getAll();
            setMaterials(response.data || []);
        } catch (err: any) {
            setError(err.message || 'Failed to load materials');
        } finally {
            setLoading(false);
        }
    };

    const filterMaterials = () => {
        let filtered = [...materials];

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (m) =>
                    m.name.toLowerCase().includes(term) ||
                    (m.code && m.code.toLowerCase().includes(term)) ||
                    (m.description && m.description.toLowerCase().includes(term))
            );
        }

        if (filterType !== 'all') {
            filtered = filtered.filter((m) => m.materialType === filterType);
        }

        setFilteredMaterials(filtered);
    };

    const handleAddClick = () => {
        setEditingMaterial(null);
        setShowModal(true);
    };

    const handleEditClick = (material: Material) => {
        setEditingMaterial(material);
        setShowModal(true);
    };

    const handleDeleteClick = (material: Material) => {
        setMaterialToDelete(material);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!materialToDelete) return;
        try {
            await materialsApi.delete(materialToDelete.id);
            setShowDeleteModal(false);
            setMaterialToDelete(null);
            blobticMessage.success(t('common.messages.success'), t('maker.msg.success.delete'));
            loadMaterials();
        } catch (err: any) {
            blobticMessage.error(t('common.messages.error'), `Failed to delete: ${err.message}`);
        }
    };

    const getMaterialTypeLabel = (type: number) => {
        switch (type) {
            case MaterialType.Aluminum: return t('maker.materials.type.aluminum');
            case MaterialType.PVC: return t('maker.materials.type.pvc');
            case MaterialType.Wood: return t('maker.materials.type.wood');
            case MaterialType.Steel: return t('maker.materials.type.steel');
            default: return t('maker.materials.type.unknown');
        }
    };

    return (
        <GenericPage
            title={t('maker.materials.title')}
            icon={<ShoppingCart />}
            footerEnabled={false}
            additionalActions={
                <Button variant="contained" startIcon={<Add />} onClick={handleAddClick}>
                    {t('maker.materials.add')}
                </Button>
            }
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <TextField
                        placeholder={t('maker.materials.search')}
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                        }}
                        sx={{ flexGrow: 1, maxWidth: 400 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value as MaterialType | 'all')}
                            displayEmpty
                        >
                            <MenuItem value="all">{t('maker.materials.filter.all')}</MenuItem>
                            <MenuItem value={MaterialType.Aluminum}>{t('maker.materials.type.aluminum')}</MenuItem>
                            <MenuItem value={MaterialType.PVC}>{t('maker.materials.type.pvc')}</MenuItem>
                            <MenuItem value={MaterialType.Wood}>{t('maker.materials.type.wood')}</MenuItem>
                            <MenuItem value={MaterialType.Steel}>{t('maker.materials.type.steel')}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <TableContainer component={Paper} sx={{ flexGrow: 1, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('maker.materials.table.code')}</TableCell>
                                <TableCell>{t('maker.materials.table.name')}</TableCell>
                                <TableCell>{t('maker.materials.table.type')}</TableCell>
                                <TableCell align="right">{t('maker.materials.table.price')}</TableCell>
                                <TableCell>{t('maker.materials.table.unit')}</TableCell>
                                <TableCell align="right">{t('maker.materials.table.stock')}</TableCell>
                                <TableCell>{t('maker.materials.table.status')}</TableCell>
                                <TableCell align="center">{t('maker.materials.table.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">{t('maker.materials.loading')}</TableCell>
                                </TableRow>
                            ) : filteredMaterials.map((material) => (
                                <TableRow key={material.id} hover>
                                    <TableCell>{material.code || '-'}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">{material.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{material.description}</Typography>
                                    </TableCell>
                                    <TableCell>{getMaterialTypeLabel(material.materialType)}</TableCell>
                                    <TableCell align="right">${material.unitPrice?.toFixed(2)}</TableCell>
                                    <TableCell>{material.unit}</TableCell>
                                    <TableCell align="right" sx={{ color: material.stockQuantity === 0 ? 'error.main' : 'inherit' }}>
                                        {material.stockQuantity?.toFixed(2)}
                                    </TableCell>
                                    <TableCell>{material.isActive ? t('maker.projects.filter.active') : t('maker.projects.filter.inactive')}</TableCell>
                                    <TableCell align="center">
                                        <IconButton size="small" onClick={() => handleEditClick(material)} color="primary"><Edit /></IconButton>
                                        <IconButton size="small" onClick={() => handleDeleteClick(material)} color="error"><Delete /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!loading && filteredMaterials.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">{t('maker.materials.empty')}</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <MaterialEditDialog
                    open={showModal}
                    onClose={() => setShowModal(false)}
                    onSuccess={loadMaterials}
                    material={editingMaterial}
                />

                <RootDialogPage
                    open={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    title={t('maker.materials.delete.title')}
                    onAction={handleDelete}
                    actionLabel={t('maker.materials.delete.action')}
                    maxWidth="xs"
                >
                    <Typography>
                        {t('maker.materials.delete.confirm', materialToDelete?.name)}
                    </Typography>
                </RootDialogPage>
            </Box>
        </GenericPage>
    );
};

export default Materials;

